import dayjs from 'dayjs'
import { ChangeEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { PromotionData } from 'domain/models/promotion'
import { labels } from 'presentation/constant/labels'
import { useCreatePromotion, useUpdatePromotion } from 'presentation/redux/hooks/promotion'
import { LIST_PROMOTIONS, urlRoutes } from 'presentation/routes/config/urlRoutes/urlRoutes'
import { validateOnlyNumber } from 'presentation/utils/format'
import { validateEmailAddress } from 'presentation/utils/validateEmailAddress'
import { validateFields } from 'presentation/utils/validateFields'
import { validateInputString } from 'presentation/utils/validateInputString'
import { validateLoginP2pString } from 'presentation/utils/validateLoginP2pString'
import { validateRuc } from 'presentation/utils/validateRuc'
import { validateSecretP2pString } from 'presentation/utils/validateSecretP2pString'

type IUsePromotionFormProps = {
  promotion?: PromotionData
  isEditPage: boolean
  rucInstitution: string
}

export const usePromotionForm = ({
  promotion,
  isEditPage,
  rucInstitution,
}: IUsePromotionFormProps) => {
  const [year, setYear] = useState<string>('')
  const [code, setCode] = useState<string>('')
  const [accountNumber, setAccountNumber] = useState<string>('')
  const [ruc, setRuc] = useState<string>('')
  const [businessName, setBusinessName] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [loginP2p, setLoginP2p] = useState<string>('')
  const [secretKeyP2p, setSecretKeyP2p] = useState<string>('')
  const [yearError, setYearError] = useState<string>('')
  const [codeError, setCodeError] = useState<string>('')
  const [accountNumberError, setAccountNumberError] = useState<string>('')
  const [rucError, setRucError] = useState<string>('')
  const [businessNameError, setBusinessNameError] = useState<string>('')
  const [emailError, setEmailError] = useState<string>('')
  const [loginP2pError, setLoginP2pError] = useState<string>('')
  const [secretKeyP2pError, setSecretKeyP2pError] = useState<string>('')
  const navigate = useNavigate()
  const params = useParams()
  const { createPromotion, errorCreatePromotion } = useCreatePromotion()
  const { updatePromotion, errorUpdatePromotion } = useUpdatePromotion()
  const institutionId = String(params.institutionId)
  const promotionId = String(params.promotionId)

  useEffect(() => {
    if (promotion?.code) {
      setCode(promotion?.code)
    } else if (rucInstitution && year) {
      setCode(`${rucInstitution}-${year}`)
    }
  }, [rucInstitution, year, promotion?.code])

  useEffect(() => {
    if (promotion) {
      setYear(promotion?.year)
      setAccountNumber(promotion?.accountNumber)
      setRuc(promotion?.ruc)
      setBusinessName(promotion?.businessName)
      setEmail(promotion?.email)
      setLoginP2p(`********${promotion?.ptpLogin}`)
      setSecretKeyP2p(`********${promotion?.ptpSecret}`)
    }
  }, [promotion])

  const handleChangeLoginP2p = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setLoginP2p(value)
    setLoginP2pError(validateLoginP2pString(value))
  }

  const handleChangeSecretKeyP2p = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setSecretKeyP2p(value)
    setSecretKeyP2pError(validateSecretP2pString(value))
  }

  const handleChangeYearPromotion = (oldValue: any, event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setYear(value)
    if (value === '') {
      setYearError(labels.form.requiredFieldText)
    } else if (!validateOnlyNumber(value)) {
      setYear(oldValue)
    } else if (value.length < 4) {
      setYearError(labels.form.minCharactersYearPromotionText)
    } else if (value.length === 4) {
      if (value < dayjs().format('YYYY')) {
        setYear(oldValue)
      } else {
        setYearError('')
      }
    } else {
      setYearError('')
    }
  }
  const handleChangCodePromotion = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setCode(value)
    setCodeError(validateFields(value))
  }
  const handleChangeAccountNumber = (oldValue: any, event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setAccountNumber(value)
    if (value === '') {
      setAccountNumberError(labels.form.requiredFieldText)
    } else if (!validateOnlyNumber(value)) {
      setAccountNumber(oldValue)
    } else {
      setAccountNumberError('')
    }
  }
  const handleChangeRuc = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setRuc(value)
    validateRuc(value, setRucError, setRuc, ruc)
  }
  const handleChangeBusinessName = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setBusinessName(value)
    validateInputString(value, setBusinessNameError, setBusinessName, businessName)
  }
  const handleChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setEmail(value)
    setEmailError(validateEmailAddress(value))
  }
  const listPromotionsUrl = `${urlRoutes.private.institutionAdministration.urlTo}/${institutionId}/${LIST_PROMOTIONS}`

  const handleCreateOrUpdateInstitution = async () => {
    const data = {
      ruc,
      businessName,
      email,
      code,
      accountNumber,
    }
    const dataCreate = {
      ...data,
      login: loginP2p,
      secret: secretKeyP2p,
      ...{ year: Number(year) },
    }
    const dataUpdate = {
      ...data,
      ...{ active: true },
    }
    const response = isEditPage
      ? await updatePromotion(promotionId, dataUpdate)
      : await createPromotion(institutionId, dataCreate)
    if (response) {
      navigate(listPromotionsUrl)
    }
  }

  const isDisabledButton =
    !code ||
    !year ||
    !email ||
    !accountNumber ||
    !ruc ||
    !businessName ||
    !loginP2p ||
    !secretKeyP2p ||
    !!yearError ||
    !!emailError ||
    !!accountNumberError ||
    !!loginP2pError ||
    !!secretKeyP2pError ||
    !!codeError ||
    !!rucError ||
    !!businessNameError

  return {
    isDisabledButton,
    handleCreateOrUpdateInstitution,
    handleChangeEmail,
    handleChangeBusinessName,
    handleChangCodePromotion,
    handleChangeRuc,
    handleChangeAccountNumber,
    handleChangeYearPromotion,
    errorCreatePromotion,
    errorUpdatePromotion,
    yearError,
    year,
    ruc,
    rucError,
    emailError,
    email,
    accountNumber,
    accountNumberError,
    listPromotionsUrl,
    code,
    codeError,
    businessName,
    businessNameError,
    loginP2p,
    secretKeyP2p,
    loginP2pError,
    secretKeyP2pError,
    handleChangeLoginP2p,
    handleChangeSecretKeyP2p,
  }
}
